import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import {
  Plus,
  Search,
  FileText,
  Trash2,
  Upload,
  Download,
  Loader2,
  Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { BulkUploadDialog } from "../components/contracts/BulkUploadDialog";
import { ContractsFilterPanel } from "../components/contracts/ContractsFilterPanel";
import { ContractTable } from "../components/contracts/ContractTable";

const statusLabels = {
  draft: "Draft", under_review: "Under Review", pending_approval: "Pending Approval",
  approved: "Approved", active: "Active", expired: "Expired",
  terminated: "Terminated", archived: "Archived",
};

const statusColors = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  under_review: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  pending_approval: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  active: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  expired: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  terminated: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  archived: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

export default function ContractsPage() {
  const navigate = useNavigate();
  const { canDo } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const [filters, setFilters] = useState({
    contract_type: "", supplier_name: "", creator_name: "",
    owner_name: "", department: "", spend_category: "", jurisdiction: "", status: "",
    date_from: "", date_to: "",
  });

  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedContracts = React.useMemo(() => {
    if (!sortField) return contracts;
    const sorted = [...contracts].sort((a, b) => {
      let va, vb;
      if (sortField === "expiration_date" || sortField === "effective_date" || sortField === "created_at") {
        va = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        vb = b[sortField] ? new Date(b[sortField]).getTime() : 0;
      } else if (sortField === "contract_value") {
        va = a.contract_value || 0;
        vb = b.contract_value || 0;
      } else if (sortField === "title") {
        va = (a.title || "").toLowerCase();
        vb = (b.title || "").toLowerCase();
      } else if (sortField === "spend_category") {
        va = (a.spend_category || "").toLowerCase();
        vb = (b.spend_category || "").toLowerCase();
      } else if (sortField === "jurisdiction") {
        va = (a.jurisdiction || "").toLowerCase();
        vb = (b.jurisdiction || "").toLowerCase();
      } else if (sortField === "status") {
        va = (a.status || "").toLowerCase();
        vb = (b.status || "").toLowerCase();
      } else {
        va = a[sortField] || "";
        vb = b[sortField] || "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [contracts, sortField, sortDir]);

  // Bulk upload state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    fetchContracts();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const resp = await apiClient.get("/contract-management/filters");
      setFilterOptions(resp.data);
    } catch (e) { /* Non-critical: filter options enhance UX but page works without them */ }
  };

  const fetchContracts = async (overrideFilters) => {
    setLoading(true);
    try {
      const f = overrideFilters || filters;
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const resp = await apiClient.get(`/contract-management/search?${params.toString()}`);
      setContracts(resp.data);
    } catch (error) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchContracts();

  const handleClearFilters = () => {
    const cleared = { contract_type: "", supplier_name: "", creator_name: "", owner_name: "", department: "", spend_category: "", jurisdiction: "", status: "", date_from: "", date_to: "" };
    setFilters(cleared);
    setSearchQuery("");
    fetchContracts(cleared);
  };

  const handleApplyFilters = () => fetchContracts();

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const response = await apiClient.get(`/contract-management/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1] : "contract_register.csv";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contract register exported");
    } catch (error) {
      toast.error("Failed to export contracts");
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handleDelete = async () => {
    if (!selectedContract) return;
    try {
      await apiClient.delete(`/contracts/${selectedContract.id}`);
      toast.success("Contract deleted successfully");
      setContracts(contracts.filter((c) => c.id !== selectedContract.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contract");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedContract(null);
    }
  };

  const handleArchive = async (contract) => {
    try {
      await apiClient.post(`/contracts/${contract.id}/archive`);
      toast.success("Contract archived successfully");
      fetchContracts();
    } catch (error) {
      toast.error("Failed to archive contract");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Contracts <HelpBubble tip="All contracts in your organisation. Create new contracts via the wizard, or clone from an existing one. Click any row to view details, track lifecycle progress, sign, and manage documents." />
          </h1>
          <p className="text-slate-500 mt-1">Manage all your contracts in one place</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} disabled={exporting} variant="outline" className="gap-2 rounded-sm active:scale-95 transition-transform" data-testid="export-register-btn">
            {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            {exporting ? "Exporting..." : "Export"}
          </Button>
          <Button onClick={() => setBulkDialogOpen(true)} variant="outline" className="gap-2 rounded-sm active:scale-95 transition-transform" data-testid="bulk-upload-btn">
            <Upload size={18} />Bulk Upload
          </Button>
          {canDo("contracts.create") && (
          <Button onClick={() => navigate("/contracts/new/wizard")} className="gap-2 rounded-sm bg-slate-900 hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-900 dark:hover:bg-cyan-400 active:scale-95 transition-transform" data-testid="create-contract-btn">
              <Plus size={18} />New Contract
          </Button>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search contracts... (e.g., 'acme IT expiring')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-10 rounded-sm"
              data-testid="search-contracts-input"
            />
          </div>
          <Button onClick={handleSearch} className="gap-2 rounded-sm" data-testid="search-btn"><Search size={16} />Search</Button>
          <Button
            variant="outline"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`gap-2 rounded-sm ${activeFilterCount > 0 ? "border-cyan-500 text-cyan-600" : ""}`}
            data-testid="toggle-filters-btn"
          >
            <Filter size={16} />Filters
            {activeFilterCount > 0 && <Badge className="ml-1 bg-cyan-500 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">{activeFilterCount}</Badge>}
          </Button>
        </div>

        {filtersOpen && (
          <ContractsFilterPanel
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
        ) : (
          <ContractTable
            contracts={sortedContracts}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            onArchive={handleArchive}
            onDeleteClick={(c) => { setSelectedContract(c); setDeleteDialogOpen(true); }}
          />
        )}
      </div>

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        contracts={contracts}
        onUploaded={fetchContracts}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Contract
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3" asChild>
              <div>
                <p>You are about to delete the following contract:</p>
                <div className="bg-slate-50 dark:bg-slate-800 border rounded-sm p-3 my-2">
                  <p className="font-medium text-slate-900 dark:text-white">{selectedContract?.title}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedContract?.contracting_party?.legal_name || "No supplier"} &middot; {statusLabels[selectedContract?.status] || selectedContract?.status}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-sm p-3 text-sm text-red-700 dark:text-red-400">
                  <p className="font-semibold mb-1">This action cannot be undone.</p>
                  <p>The contract and all associated metadata will be permanently removed from the register. If you want to retain the record, consider using <strong>Archive</strong> instead.</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
